import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Check, ChevronRight, Users, MapPin, Heart, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import SearchableSelect from '@/components/SearchableSelect';
import { ALL_COUNTRIES as COUNTRIES } from '@/constants/countries';
import { NATIONALITIES } from '@/constants';
import { INTEREST_CATEGORIES, CATEGORY_STYLES } from '@/constants/interests';

const STEPS = [
  { id: 'profile', label: 'Complete Profile', icon: MapPin },
  { id: 'groups', label: 'Join Groups', icon: Users },
  { id: 'interests', label: 'Pick Interests', icon: Heart },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromEmail = searchParams.get('ref') === 'engagement-email';
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [profile, setProfile] = useState({
    nationality: '',
    birth_country: '',
    birth_city: '',
    residence_country: '',
    residence_city: '',
    about_me: '',
  });

  // Groups
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // Interests
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Pre-fill from user data
  useEffect(() => {
    if (user) {
      setProfile({
        nationality: user.nationality || '',
        birth_country: user.birth_country || '',
        birth_city: user.birth_city || '',
        residence_country: user.residence_country || '',
        residence_city: user.residence_city || '',
        about_me: user.about_me || '',
      });
      setSelectedInterests(user.interests || []);
      setMyGroups(user.groups || []);
    }
  }, [user]);

  // Fetch groups when step 2
  useEffect(() => {
    if (step === 1) fetchGroups();
  }, [step]);

  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const { data } = await axios.get(`${API}/groups`);
      const publicGroups = (data.public_groups || data || []).filter(g => !g.is_private);
      setGroups(publicGroups.sort((a, b) => a.name.localeCompare(b.name)));
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me/profile`, profile);
      await refreshUser();
      setStep(1);
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleJoinGroup = async (slug) => {
    try {
      await axios.post(`${API}/groups/${slug}/join`);
      setMyGroups(prev => [...prev, slug]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join group');
    }
  };

  const handleLeaveGroup = async (slug) => {
    try {
      await axios.post(`${API}/groups/${slug}/leave`);
      setMyGroups(prev => prev.filter(s => s !== slug));
    } catch {
      toast.error('Failed to leave group');
    }
  };

  const handleSaveInterests = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me/profile`, { interests: selectedInterests });
      await refreshUser();
      toast.success("You're all set! Welcome to ExpatsSphere!");
      navigate('/');
    } catch {
      toast.error('Failed to save interests');
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const filledProfileFields = [profile.residence_country, profile.residence_city, profile.nationality].filter(Boolean).length;

  // Map interest categories to group categories for recommendations
  const INTEREST_TO_GROUP_CATEGORY = {
    'Fit & Fun': 'Sports & Fitness',
    'Bites & Bright Lights': 'Food & Drinks',
    'Business & Beyond': 'Professional',
    'Arts & Culture': 'Arts & Entertainment',
    'More to Explore': 'Travel & Adventure',
  };

  // Interest keywords → group slug direct matches
  const INTEREST_KEYWORD_TO_GROUP = {
    'hiking': 'hiking', 'running & walking': 'hiking',
    'photography': 'photography',
    'cooking': 'foodies', 'dining out': 'foodies', 'lunch & brunch': 'foodies',
    'wine': 'wine', 'cocktails & longdrinks': 'wine', 'beer': 'wine',
    'professional networking': 'networking', 'entrepreneurship & start ups': 'networking', 'business & law': 'networking',
    'yoga & wellness': 'yoga', 'health & wellness': 'yoga', 'fitness': 'yoga',
    'soccer': 'sports', 'basketball': 'sports', 'tennis': 'sports', 'volleyball': 'sports', 'swimming': 'sports', 'cycling': 'sports',
    'arts': 'art-culture', 'museums & exhibitions': 'art-culture', 'painting & drawing': 'art-culture',
    'literature & creative writing': 'books',
    'live music & concerts': 'music', 'making music': 'music', 'jazz music': 'music', 'classical music': 'music',
    'languages & cultures': 'language',
  };

  // Get recommended group slugs based on user's existing interests
  const getRecommendedGroupSlugs = () => {
    const userInterests = selectedInterests.length > 0 ? selectedInterests : (user?.interests || []);
    if (userInterests.length === 0) return new Set();
    const slugs = new Set();
    // Direct keyword matches
    userInterests.forEach(interest => {
      const slug = INTEREST_KEYWORD_TO_GROUP[interest.toLowerCase()];
      if (slug) slugs.add(slug);
    });
    // Category-based matches
    for (const [category, interests] of Object.entries(INTEREST_CATEGORIES)) {
      const hasMatch = userInterests.some(i => interests.includes(i));
      if (hasMatch) {
        const groupCat = INTEREST_TO_GROUP_CATEGORY[category];
        if (groupCat) {
          groups.forEach(g => { if (g.category === groupCat) slugs.add(g.slug); });
        }
      }
    }
    return slugs;
  };

  const recommendedSlugs = getRecommendedGroupSlugs();
  const recommendedGroups = groups.filter(g => recommendedSlugs.has(g.slug));
  const otherGroups = groups.filter(g => !recommendedSlugs.has(g.slug));

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-purple-50/30 to-amber-50/20">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-foreground">
              <span className="text-primary italic">ExpatsSphere</span> — Getting Started
            </h2>
            <span className="text-xs text-muted-foreground">Step {step + 1} of 3</span>
          </div>
          <div className="flex gap-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex-1 flex items-center gap-1.5">
                <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                  i < step ? 'bg-primary' : i === step ? 'bg-primary/60' : 'bg-muted'
                }`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1.5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <span key={s.id} className={`text-[10px] flex items-center gap-1 ${
                  i <= step ? 'text-primary font-medium' : 'text-muted-foreground'
                }`}>
                  {i < step ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  {s.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="pt-28 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {/* STEP 1: Complete Profile */}
            {step === 0 && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-3xl border-2 border-border p-6 md:p-8 shadow-sm">
                  <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                      Tell us about yourself
                    </h1>
                    <p className="text-muted-foreground text-sm">
                      Help other expats find and connect with you. Where are you based?
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="residence_country">Country you live in</Label>
                        <SearchableSelect
                          options={COUNTRIES}
                          value={profile.residence_country}
                          onValueChange={(v) => setProfile(p => ({ ...p, residence_country: v }))}
                          placeholder="Select country"
                          data-testid="onboard-country"
                        />
                      </div>
                      <div>
                        <Label htmlFor="residence_city">City</Label>
                        <Input
                          value={profile.residence_city}
                          onChange={(e) => setProfile(p => ({ ...p, residence_city: e.target.value }))}
                          placeholder="e.g. Budapest, London"
                          data-testid="onboard-city"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="nationality">Nationality</Label>
                      <SearchableSelect
                        options={NATIONALITIES}
                        value={profile.nationality}
                        onValueChange={(v) => setProfile(p => ({ ...p, nationality: v }))}
                        placeholder="Select nationality"
                        data-testid="onboard-nationality"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="birth_country">Country of birth <span className="text-muted-foreground text-xs">(optional)</span></Label>
                        <SearchableSelect
                          options={COUNTRIES}
                          value={profile.birth_country}
                          onValueChange={(v) => setProfile(p => ({ ...p, birth_country: v }))}
                          placeholder="Select country"
                        />
                      </div>
                      <div>
                        <Label htmlFor="birth_city">City of birth <span className="text-muted-foreground text-xs">(optional)</span></Label>
                        <Input
                          value={profile.birth_city}
                          onChange={(e) => setProfile(p => ({ ...p, birth_city: e.target.value }))}
                          placeholder="e.g. Lagos, Mumbai"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="about_me">About you <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Textarea
                        value={profile.about_me}
                        onChange={(e) => setProfile(p => ({ ...p, about_me: e.target.value }))}
                        placeholder="A few words about yourself — hobbies, what brought you abroad..."
                        rows={3}
                        data-testid="onboard-about"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-8">
                    <button
                      onClick={() => { navigate('/'); }}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="onboard-skip-profile"
                    >
                      Skip for now
                    </button>
                    <Button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="rounded-full px-8 h-11 font-bold"
                      data-testid="onboard-save-profile"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Continue
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Join Groups */}
            {step === 1 && (
              <motion.div
                key="groups"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-3xl border-2 border-border p-6 md:p-8 shadow-sm">
                  <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                      Join groups you're interested in
                    </h1>
                    <p className="text-muted-foreground text-sm">
                      You'll get notified about events in the groups you join. Pick a few to get started!
                    </p>
                  </div>

                  {loadingGroups ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-3" />
                      <p className="text-muted-foreground text-sm">Loading groups...</p>
                    </div>
                  ) : (
                    <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-4">
                      {/* Recommended Groups */}
                      {recommendedGroups.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-semibold text-amber-700">Recommended for you</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {recommendedGroups.map((group) => {
                              const isJoined = myGroups.includes(group.slug);
                              return (
                                <button
                                  key={group.slug}
                                  onClick={() => isJoined ? handleLeaveGroup(group.slug) : handleJoinGroup(group.slug)}
                                  className={`flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ring-2 ring-amber-200/60 ${
                                    isJoined
                                      ? 'border-primary bg-primary/5 shadow-sm'
                                      : 'border-amber-200 hover:border-primary/40 bg-amber-50/40'
                                  }`}
                                  data-testid={`onboard-group-${group.slug}`}
                                >
                                  <span className="text-2xl flex-shrink-0">{group.icon}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{group.name}</p>
                                    <p className="text-xs text-muted-foreground">{group.member_count || 0} members</p>
                                  </div>
                                  {isJoined && (
                                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                      <Check className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Other Groups */}
                      {otherGroups.length > 0 && (
                        <div>
                          {recommendedGroups.length > 0 && (
                            <p className="text-xs font-medium text-muted-foreground mb-2 mt-1">All groups</p>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {otherGroups.map((group) => {
                              const isJoined = myGroups.includes(group.slug);
                              return (
                                <button
                                  key={group.slug}
                                  onClick={() => isJoined ? handleLeaveGroup(group.slug) : handleJoinGroup(group.slug)}
                                  className={`flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ${
                                    isJoined
                                      ? 'border-primary bg-primary/5 shadow-sm'
                                      : 'border-border hover:border-primary/40 hover:bg-muted/30'
                                  }`}
                                  data-testid={`onboard-group-${group.slug}`}
                                >
                                  <span className="text-2xl flex-shrink-0">{group.icon}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{group.name}</p>
                                    <p className="text-xs text-muted-foreground">{group.member_count || 0} members</p>
                                  </div>
                                  {isJoined && (
                                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                      <Check className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-8">
                    <button
                      onClick={() => setStep(2)}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="onboard-skip-groups"
                    >
                      Skip for now
                    </button>
                    <Button
                      onClick={() => setStep(2)}
                      className="rounded-full px-8 h-11 font-bold"
                      data-testid="onboard-continue-groups"
                    >
                      Continue
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 3: Select Interests */}
            {step === 2 && (
              <motion.div
                key="interests"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-3xl border-2 border-border p-6 md:p-8 shadow-sm">
                  <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                      What are you into?
                    </h1>
                    <p className="text-muted-foreground text-sm">
                      Select your interests so we can personalize event recommendations for you.
                    </p>
                  </div>

                  <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                    {Object.entries(INTEREST_CATEGORIES).map(([category, interests]) => {
                      const style = CATEGORY_STYLES[category] || {};
                      const selectedCount = selectedInterests.filter(i => interests.includes(i)).length;
                      const isExpanded = expandedCategory === category;

                      return (
                        <div key={category} className="rounded-2xl border border-border overflow-hidden">
                          <button
                            onClick={() => setExpandedCategory(isExpanded ? null : category)}
                            className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                              selectedCount > 0 ? 'bg-primary/5' : 'hover:bg-muted/50'
                            }`}
                            data-testid={`onboard-interest-cat-${category.replace(/\s/g, '-')}`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{category}</span>
                              {selectedCount > 0 && (
                                <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full font-medium">
                                  {selectedCount}
                                </span>
                              )}
                            </div>
                            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              transition={{ duration: 0.2 }}
                              className="px-3 pb-3"
                            >
                              <div className="flex flex-wrap gap-2 pt-1">
                                {interests.map((interest) => {
                                  const isSelected = selectedInterests.includes(interest);
                                  return (
                                    <button
                                      key={interest}
                                      onClick={() => toggleInterest(interest)}
                                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                        isSelected
                                          ? (style.selectedColor || 'bg-primary text-white border-primary')
                                          : (style.tagColor || 'bg-muted text-foreground border-border hover:border-primary/40')
                                      }`}
                                      data-testid={`onboard-interest-${interest.replace(/\s/g, '-')}`}
                                    >
                                      {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                                      {interest}
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {selectedInterests.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-3">
                      {selectedInterests.length} interest{selectedInterests.length !== 1 ? 's' : ''} selected
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-8">
                    <button
                      onClick={() => { navigate('/'); }}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="onboard-skip-interests"
                    >
                      Skip for now
                    </button>
                    <Button
                      onClick={handleSaveInterests}
                      disabled={saving}
                      className="rounded-full px-8 h-11 font-bold"
                      data-testid="onboard-finish"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {selectedInterests.length > 0 ? "Let's Go!" : 'Finish'}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
